import { Award, QrCode, Search, ShieldCheck, ArrowLeft, CheckCircle, XCircle, Clock, MapPin, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { verifyCertificate } from "../api/greenEnergy";

export function VerifyPage() {
  const [certificateNumber, setCertificateNumber] = useState("");
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "verifying" | "verified" | "not-found">("idle");
  const [certificateData, setCertificateData] = useState<any>(null);
  const [errors, setErrors] = useState<string | null>(null);

  const formatDate = (dateStr: string): string => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const formatPeriod = (start: string, end: string): string => {
    return `${formatDate(start)} – ${formatDate(end)}`;
  };

  const handleVerify = async () => {
    const trimmed = certificateNumber.trim();
    if (!trimmed) {
      setErrors("Please enter a certificate number.");
      return;
    }
    setErrors(null);
    setVerificationStatus("verifying");
    setCertificateData(null);
    try {
      const response = await verifyCertificate(trimmed);
      if (response.success && response.data?.isValid) {
        setCertificateData(response.data);
        setVerificationStatus("verified");
      } else {
        setVerificationStatus("not-found");
      }
    } catch (err: any) {
      setErrors(
        err.response?.data?.message ||
        err.message ||
        "Unable to verify certificate. Please check the certificate number and try again."
      );
      setVerificationStatus("not-found");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a
              href="/"
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </a>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
              <span className="text-lg">Certificate Verification</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
              Verify Your GEC Certificate
            </h1>
            <p className="text-lg text-gray-600">
              Enter your certificate number or scan QR code for instant blockchain verification
            </p>
          </div>

          {/* Verification Form */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Input Method */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-8 rounded-2xl border-2 border-emerald-200 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg text-gray-900">Enter Certificate Number</h3>
                  <p className="text-xs text-gray-600">Type or paste your certificate ID</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="cert-number" className="block text-sm text-gray-700 mb-2">
                    Certificate Number
                  </label>
                  <div className="relative">
                    <input
                      id="cert-number"
                      type="text"
                      value={certificateNumber}
                      onChange={(e) => {
                        setCertificateNumber(e.target.value);
                        setVerificationStatus("idle");
                        setErrors(null);
                      }}
                      placeholder="e.g., GEC-2025-001234"
                      className="w-full px-4 py-3 pr-12 rounded-lg border-2 border-emerald-200 focus:border-emerald-500 focus:outline-none bg-white transition-colors"
                    />
                    <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                <button
                  onClick={handleVerify}
                  disabled={verificationStatus === "verifying" || !certificateNumber.trim()}
                  className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {verificationStatus === "verifying" ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    "Verify Certificate"
                  )}
                </button>
              </div>
            </motion.div>

            {/* QR Scanner */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 rounded-2xl border-2 border-cyan-200 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg text-gray-900">Scan QR Code</h3>
                  <p className="text-xs text-gray-600">Use your camera to scan</p>
                </div>
              </div>

              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center mb-4">
                <div className="text-center">
                  <QrCode className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">QR Scanner Interface</p>
                </div>
              </div>

              <button className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:shadow-xl transition-shadow">
                Open Camera
              </button>
            </motion.div>
          </div>

          {/* Verification Results */}
          {verificationStatus === "verified" && certificateData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-emerald-50 to-cyan-50 p-8 rounded-2xl border-2 border-emerald-300 shadow-xl"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl text-emerald-900 mb-2">Certificate Verified Successfully!</h3>
                  <p className="text-emerald-700">
                    This certificate is valid and blockchain-verified. All details have been authenticated.
                  </p>
                </div>
              </div>

              {/* Certificate Details */}
              <div className="bg-white rounded-xl p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Certificate Number</p>
                    <p className="text-sm">{certificateData.certificateNo}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Status</p>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <p className="text-sm text-emerald-600">{certificateData.status} & Verified</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Company</p>
                    <p className="text-sm">{certificateData.company?.name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Period</p>
                    <p className="text-sm">
                      {certificateData.period
                        ? formatPeriod(certificateData.period.startDate, certificateData.period.endDate)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Green Consumed Units</p>
                    <p className="text-sm">
                      {certificateData.totalGreenEnergyUnit != null
                        ? `${Math.round(certificateData.totalGreenEnergyUnit)} kWh`
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Issued Date</p>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <p className="text-sm">
                        {certificateData.issuedAt ? formatDate(certificateData.issuedAt) : "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Energy Sources */}
                {certificateData.sources && certificateData.sources.length > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-600 mb-2">Energy Sources</p>
                    <div className="flex flex-wrap gap-2">
                      {certificateData.sources.map((source: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium capitalize"
                        >
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Company Info */}
                {certificateData.company && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{certificateData.company.name}</span>
                      {certificateData.company.companyCode && (
                        <span className="text-xs text-gray-400 ml-1">({certificateData.company.companyCode})</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Details */}
                <div className="pt-4 border-t border-gray-200 grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Consumer Number</p>
                    <p className="text-sm">{certificateData.consumerNo || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Certificate ID</p>
                    <p className="text-sm">{certificateData.certificateId || "—"}</p>
                  </div>
                </div>
              </div>

              {/* Blockchain Info */}
              <div className="mt-6 p-4 bg-emerald-100 rounded-lg border border-emerald-300 space-y-2">
                {certificateData.transactionHash && (
                  <p className="text-sm text-emerald-900">
                    <strong>Blockchain Hash:</strong> {certificateData.transactionHash}
                  </p>
                )}
                {certificateData.blockNumber && (
                  <p className="text-sm text-emerald-900">
                    <strong>Block Number:</strong> {certificateData.blockNumber}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {verificationStatus === "not-found" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-2xl border-2 border-red-300 shadow-xl"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl text-red-900 mb-2">Certificate Not Found</h3>
                  <p className="text-red-700 mb-4">
                    {errors || "We couldn't find a certificate matching this number. Please check the number and try again."}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-gray-700 mb-2">Common issues:</p>
                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                      <li>Check for typos in the certificate number</li>
                      <li>Ensure the format is correct (e.g., GEC-88610254348-20260702-20260731)</li>
                      <li>Certificate may not be issued yet</li>
                      <li>Contact support if the issue persists</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Info Cards */}
          {verificationStatus === "idle" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="grid md:grid-cols-3 gap-6"
            >
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="text-lg mb-2 text-gray-900">Instant Verification</h4>
                <p className="text-sm text-gray-600">
                  Real-time blockchain verification ensures authenticity in seconds.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="text-lg mb-2 text-gray-900">Globally Accepted</h4>
                <p className="text-sm text-gray-600">
                  Certificates are recognized worldwide for ESG compliance.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-lg mb-2 text-gray-900">Tamper-Proof</h4>
                <p className="text-sm text-gray-600">
                  Immutable blockchain records prevent any alterations.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
